import { formatParkCode } from '../net/protocol';
import { controlsHelp } from './controlsHelp';
import { clear, h } from './dom';
import type { Settings } from './Phone';
import { settingsControls } from './settingsForm';

export interface TitleHost {
  /** "New Game" (after it's been confirmed, if there's a save to replace). */
  onNewGame(): void;
  onContinue(): void;
  /** Don't join the multiplayer park this time. */
  onSkipPark(): void;
  settings(): Settings;
  onSettings(s: Settings): void;
}

type Page = 'menu' | 'confirm' | 'howto' | 'settings' | 'credits';

/**
 * The start screen: Lot 7 at sundown behind the logo, and a plain menu. New Game goes to
 * the character creator (a screen of its own); everything else stays right here.
 */
export class TitleScreen {
  readonly el: HTMLDivElement;
  private readonly menu: HTMLElement;
  private page: Page = 'menu';
  private continueInfo: string | null = null;
  private park: { code: string; invited: boolean } | null = null;

  constructor(
    parent: HTMLElement,
    private readonly host: TitleHost,
  ) {
    this.menu = h('nav', { class: 'title-menu cardboard interactive', attrs: { 'aria-label': 'Main menu' } });
    this.el = h(
      'div',
      { class: 'title-screen', hidden: true },
      h(
        'div',
        { class: 'title-left' },
        h('div', { class: 'logo' }, 'DOUBLE WIDE DREAMS', h('small', { text: 'A TRAILER PARK SIMULATOR' })),
        h('div', {
          class: 'tagline',
          text: 'Welcome to Lucky Acres, Lot 7. Your uncle left you a single-wide, a Camaro on cinder blocks and a cooler of warm beer. Pay your lot rent. Survive tornado season. Chase the dream: a DOUBLE WIDE.',
        }),
        h('div', {
          class: 'disclaimer',
          text: 'An affectionate parody in the tradition of Foxworthy, Joe Dirt and Trailer Park Boys. Made by and for people who know a porch couch is a perfectly good couch. Contains cartoon beer, fireworks and private equity.',
        }),
      ),
      this.menu,
    );
    this.el.addEventListener('keydown', (e) => this.onKey(e));
    parent.append(this.el);
  }

  /** `continueInfo` describes the save you'd continue ("Dwayne, Day 4"), or null for none. */
  show(continueInfo: string | null): void {
    this.continueInfo = continueInfo;
    this.el.hidden = false;
    this.go('menu');
  }

  hide(): void {
    this.el.hidden = true;
  }

  get isOpen(): boolean {
    return !this.el.hidden;
  }

  /** Shows which multiplayer park you'll join once you start playing (or none). */
  setPark(code: string | null, invited = false): void {
    this.park = code ? { code, invited } : null;
    if (this.isOpen) this.render();
  }

  private go(page: Page): void {
    this.page = page;
    this.render();
    // Keyboards and controllers land on the first thing to press.
    const first = this.menu.querySelector<HTMLElement>('.menu-item, .tape');
    first?.focus({ preventScroll: true });
  }

  /** Up and down move through the buttons; Escape goes back a page. */
  private onKey(e: KeyboardEvent): void {
    if (this.el.hidden) return;
    if (e.key === 'Escape' && this.page !== 'menu') {
      e.preventDefault();
      this.go('menu');
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    if ((e.target as HTMLElement | null)?.tagName === 'INPUT') return;
    const items = [...this.menu.querySelectorAll<HTMLElement>('button:not(:disabled)')];
    if (!items.length) return;
    const at = items.indexOf(document.activeElement as HTMLElement);
    const next = e.key === 'ArrowDown' ? (at + 1) % items.length : (at - 1 + items.length) % items.length;
    items[at < 0 ? 0 : next]?.focus();
    e.preventDefault();
  }

  private item(
    icon: string,
    label: string,
    sub: string | null,
    run: () => void,
    cls = '',
  ): HTMLButtonElement {
    return h(
      'button',
      { class: `menu-item tape ${cls}`.trim(), on: { click: run } },
      h('span', { class: 'menu-icon', text: icon }),
      h(
        'span',
        { class: 'menu-text' },
        h('span', { class: 'menu-label', text: label }),
        sub ? h('span', { class: 'menu-sub', text: sub }) : null,
      ),
    );
  }

  private back(): HTMLButtonElement {
    return h('button', {
      class: 'tape menu-back',
      text: ':back: Back',
      on: { click: () => this.go('menu') },
    });
  }

  private render(): void {
    clear(this.menu);
    this.menu.dataset.page = this.page;
    const park = this.park;
    if (park && (this.page === 'menu' || this.page === 'confirm')) {
      this.menu.append(
        h(
          'div',
          { class: 'invite-banner' },
          h('span', {
            text: park.invited
              ? `:camp: You're invited to park ${formatParkCode(park.code)}! Start or continue a game and you'll pull right in.`
              : `:camp: You'll head back to park ${formatParkCode(park.code)} when you jump back in.`,
          }),
          h('button', {
            class: 'invite-skip',
            text: '✕',
            title: 'Play solo instead',
            attrs: { 'aria-label': 'Play solo instead' },
            on: { click: () => this.host.onSkipPark() },
          }),
        ),
      );
    }
    switch (this.page) {
      case 'menu':
        this.renderMenu();
        break;
      case 'confirm':
        this.menu.append(
          h('h2', { text: 'Start over?' }),
          h('p', {
            class: 'menu-copy',
            text: `You've got a game going (${this.continueInfo ?? 'saved'}). Moving a new redneck into Lot 7 replaces it for good.`,
          }),
          h(
            'div',
            { class: 'menu-list' },
            this.item(
              ':trailer:',
              'Start over',
              'Build a new redneck',
              () => this.host.onNewGame(),
              'danger',
            ),
            this.item(':back:', 'Never mind', null, () => this.go('menu')),
          ),
        );
        break;
      case 'howto':
        this.menu.append(
          h('h2', { text: 'How to Play' }),
          h('div', { class: 'menu-scroll' }, ...controlsHelp({ dream: true, tips: 6 })),
          this.back(),
        );
        break;
      case 'settings':
        this.menu.append(
          h('h2', { text: 'Settings' }),
          h(
            'div',
            { class: 'menu-scroll settings-form' },
            ...settingsControls(this.host.settings(), (s) => this.host.onSettings(s)),
          ),
          this.back(),
        );
        break;
      case 'credits':
        this.menu.append(
          h('h2', { text: 'Credits' }),
          h(
            'div',
            { class: 'menu-scroll credits' },
            h('p', { text: 'Double Wide Dreams: a trailer park simulator.' }),
            h('p', {
              text: 'An affectionate parody in the tradition of Foxworthy, Joe Dirt and Trailer Park Boys. Any resemblance to your cousin is purely coincidental.',
            }),
            h('h3', { text: 'Made with' }),
            h('p', {
              text: 'three.js, TypeScript and Vite. Every icon is hand-drawn for the game. Fonts from Google Fonts: Rye, Bangers, Anton, Permanent Marker, Pacifico and Comic Neue.',
            }),
            h('h3', { text: 'Thanks' }),
            h('p', {
              text: 'To everybody who ever fixed a Camaro with parts that were "allegedly compatible," and to Big Earl, whose grill has outlived two marriages and a house fire.',
            }),
          ),
          this.back(),
        );
        break;
    }
  }

  private renderMenu(): void {
    const saved = this.continueInfo;
    const list = h('div', { class: 'menu-list' });
    if (saved) list.append(this.item(':play:', 'Continue', saved, () => this.host.onContinue(), 'primary'));
    list.append(
      this.item(
        ':trailer:',
        'New Game',
        saved ? null : 'Move in to Lot 7',
        () => (saved ? this.go('confirm') : this.host.onNewGame()),
        saved ? '' : 'primary',
      ),
      this.item(':question:', 'How to Play', null, () => this.go('howto')),
      this.item(':gear:', 'Settings', null, () => this.go('settings')),
      this.item(':scroll:', 'Credits', null, () => this.go('credits')),
    );
    this.menu.append(list);
  }
}
